import { useState, useRef, useEffect } from 'react';
import { Box, IconButton, useMediaQuery, SwipeableDrawer } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useAuth from '../hooks/useAuth';
import { AdminRoutes, Routes, S3Folders } from '../Static/constants';
import { isRouteActive, keycloakLogout } from '../utils/common';
import { useMsal } from '@azure/msal-react';
import { ModuleNames } from '../Static/constants';
import { hasAnyAuthority } from '../utils/hasAuthority';
import { uploadFiletoS3 } from '../services/s3Service';
import updateUserAvatar from '../api/requests/updateUserAvatar';
import getUserProfile from '../api/requests/getUserProfile';
export const { REACT_APP_BE_DOC_URL = '' } = process.env;

const MenuIcon = '/images/menu-icon.svg';
const defaultUser = '/images/default_user.svg';

const NavContainer = styled.div`
    background:#FFFFFF;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-left: 0rem;
    padding-right: 0.5rem;
    min-height: 100vh;    
    border-radius: 0px 24px 24px 0px;
    width: calc(15vw + 5rem);
    padding-bottom: 3rem;
`
const TopNavigationContainer = styled.div`
    background:#FFFFFF;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 1.5rem;
    box-shadow: 0 1px 10px rgb(0 0 0 / 0.15);
`;
const NavListContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 3rem;
    width: calc(15vw + 2rem);
`
const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 3rem;
    @media (max-width: 751px){
      margin: 0;
    }
`
const RoutesListContainer = styled.div`
    display: flex;
    align-items: start;
    flex-direction: column;
    color: white;
    width: 100%;
    gap: 1rem;
    padding: 0rem 0.5rem;
`

const ListItem = styled.div<{ $active: boolean }>`
    padding: 0.5rem 0.5rem;
    display: flex;
    color: #008BAA;
    align-items: center;
    background-color: ${props => props.$active ? '#CCF6FF' : ''};
    cursor: pointer;
    border-radius: 8px;
    width: 90%;
    && span {
      font-weight: ${props => props.$active ? '600' : '400'};
      color: ${props => props.$active ? '#008BAA' : '#B3B3B3'} ;
    }
    && svg {
      vertical-align: bottom;
      padding-right: 0.5rem;
    }
    &&:hover span {
      color: #008BAA;
    }
    && svg path {
      fill: ${props => props.$active ? '#008BAA' : '#E3E3E3'};
    }
    &&:hover svg path {
      fill: #008BAA;
    }
`

const DrawerConatiner = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  gap: 1.5rem;
`

const LogoutContainer = styled.div`
display: flex;
gap: 1rem;
padding: 0.5rem 0.75rem;
cursor: pointer;
align-items: center;
&&:hover svg path{
  fill: #008BAA;
}
&& p{
  font-size: 14px;
  font-weight: 500;
}
`;

const NavigateContainer = styled.div`
position: relative;
display: flex;
align-items: center;
gap: 0.5rem;
&& span:hover span{
    visibility: visible;
    background-color: rgba(0,0,0,0.5);
    border-radius: 50%;
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}
&& input{
    display: none;
}
&& p{
  font-size: 14px;
  font-weight: 500;
}
`

const ProfilePicContainer = styled.span`
position: relative;
display: flex;
&& img{
    vertical-align: bottom;    
    cursor: pointer;  
    border-radius: 50%;  
}
&& .profile-pic{
    position: relative;
}
&& span{
    position: absolute;
    left: 0px;
    top: 0px;
    visibility: hidden;
}
&& span .upload-pic{
    padding: 0;
}
`

const HCLEnterpriseLogo = '/images/logo.jpg';
const uploadLogo = '/images/upload_logo.svg';

const LeftNavigationBar = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 751px)');
  const [isDrawer, setIsDrawer] = useState<boolean>(false);
  const { user, privileges } = useAuth();
  const { instance } = useMsal();
  const inputRef = useRef<HTMLInputElement>(null);
  const [profileImg, setProfileImg] = useState<string>();

  const handleLogout = () => {
    // instance.logoutRedirect({
    //   postLogoutRedirectUri: "/"
    // });
    keycloakLogout();
  }
  const uploadHandler = () => {
    inputRef.current?.click();
}

  const toggleDrawer = (open: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {        
        return;
      }
      setIsDrawer(open);
      return open;
    };

  const profilePicHandler = (event: any) => { 
    event.preventDefault();
    const file = event.target.files[0];   
    uploadFiletoS3(file, S3Folders.profiles).then(async (data: any) => {
        await updateUserAvatar({ profilePicture: data.key, userId: user?.id as string });
        const profileData = await getUserProfile();
        setProfileImg(profileData?.data?.user?.profilePicture)
        setIsDrawer(false);
    }).catch((err: Error) => {
        // toast.error(err?.message);
    });
}

useEffect(() => {
  setProfileImg(user?.profilePicture ?? '');
},[user])

let profilePic = profileImg;
if (!profileImg?.includes(REACT_APP_BE_DOC_URL)) {
  profilePic = REACT_APP_BE_DOC_URL + '/' + profileImg;
}

const routesList = () => {
  return (
    <RoutesListContainer>
      <>
        {
          AdminRoutes.map((item) => (item.privileges?.length && hasAnyAuthority(privileges, item?.privileges)) &&
          // Restrict the Reports route for Delivery Manager and Lead
            <ListItem
              key={item.title}
              $active={isRouteActive(item)}
              onClick={() => {navigate(item.path); setIsDrawer(false);}}
            >
              {item?.icon}
              <span>{item.title}</span>
            </ListItem>
          )
        }
      </>
    </RoutesListContainer>
  )
}

  return (<>
    {
      !isMobile &&
      <NavContainer>
        <NavListContainer>
          { user?.module && <LogoContainer>
            <img src={user?.module === ModuleNames.ENTERPRISE ? user?.enterprises[0]?.logo?.includes(REACT_APP_BE_DOC_URL) ? user?.enterprises[0]?.logo : REACT_APP_BE_DOC_URL + '/' + user?.enterprises[0]?.logo : HCLEnterpriseLogo} alt='' height={user?.module === ModuleNames.ENTERPRISE ? 80 : 50} width={user?.module === ModuleNames.ENTERPRISE ? 0 : 180} />
          </LogoContainer>}
          <RoutesListContainer>
            {routesList()}
          </RoutesListContainer>
        </NavListContainer>
      </NavContainer>
    }
    {
      isMobile &&
      <TopNavigationContainer>
        <LogoContainer>
          <img src={user?.module === ModuleNames.ENTERPRISE ? user?.enterprises[0]?.logo?.includes(REACT_APP_BE_DOC_URL) ? user?.enterprises[0]?.logo : REACT_APP_BE_DOC_URL + '/' + user?.enterprises[0]?.logo : HCLEnterpriseLogo} alt='' height={user?.module === ModuleNames.ENTERPRISE ? 80 : 50} />
        </LogoContainer>
        <IconButton onClick={() => setIsDrawer(true)}>
          <img src={MenuIcon} alt='' width={20} height={12} />
        </IconButton>
        <SwipeableDrawer
          anchor='right'
          open={isDrawer}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
        >
          <DrawerConatiner
            sx={{ width: 250, padding: '1.5rem 1.5rem' }}
            role="presentation"
          >
            {routesList()}
            <span>
            <NavigateContainer>
              <ProfilePicContainer>
                <img className='profile-pic' src={profileImg ? profilePic : defaultUser} alt='User Profile' height={40} width={40} />
                <span><img className='upload-pic' src={uploadLogo} alt='upload' height={30} width={30} onClick={uploadHandler} /></span>
                <input ref={inputRef} type='file' accept='image/*' onChange={profilePicHandler} />
                </ProfilePicContainer>
                <p>{user?.name}</p>
            </NavigateContainer>
            <LogoutContainer onClick={() => handleLogout()}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.1996 4.20036C13.1996 4.11387 13.1808 4.02842 13.1447 3.94985C13.1086 3.87128 13.0559 3.80145 12.9902 3.74514C12.9246 3.68884 12.8475 3.6474 12.7644 3.62365C12.6812 3.5999 12.5939 3.59441 12.5084 3.60756L4.10841 4.89156C3.96679 4.91334 3.83764 4.98508 3.74432 5.09381C3.65099 5.20254 3.59966 5.34107 3.59961 5.48436V18.5164C3.59966 18.6596 3.65099 18.7982 3.74432 18.9069C3.83764 19.0156 3.96679 19.0874 4.10841 19.1092L12.5084 20.3944C12.5939 20.4075 12.6812 20.402 12.7644 20.3783C12.8475 20.3545 12.9246 20.3131 12.9902 20.2568C13.0559 20.2005 13.1086 20.1306 13.1447 20.0521C13.1808 19.9735 13.1996 19.888 13.1996 19.8016V12.0004H19.406L18.2096 13.0492C18.097 13.1558 18.03 13.3018 18.0227 13.4567C18.0155 13.6116 18.0684 13.7633 18.1705 13.88C18.2726 13.9967 18.416 14.0693 18.5705 14.0827C18.725 14.096 18.8786 14.049 18.9992 13.9516L21.3944 11.8516C21.4587 11.7952 21.5101 11.7259 21.5454 11.648C21.5807 11.5702 21.5989 11.4858 21.5989 11.4004C21.5989 11.3149 21.5807 11.2305 21.5454 11.1527C21.5101 11.0749 21.4587 11.0055 21.3944 10.9492L18.9992 8.84916C18.8795 8.74445 18.7232 8.69157 18.5645 8.70215C18.4059 8.71272 18.2579 8.78589 18.1532 8.90556C18.0485 9.02522 17.9956 9.18158 18.0062 9.34024C18.0168 9.49889 18.0899 9.64685 18.2096 9.75156L19.406 10.8004H13.1996V4.20036ZM10.1996 13.5004C9.96091 13.5004 9.732 13.4055 9.56321 13.2368C9.39443 13.068 9.29961 12.8391 9.29961 12.6004C9.29961 12.3617 9.39443 12.1327 9.56321 11.964C9.732 11.7952 9.96091 11.7004 10.1996 11.7004C10.4383 11.7004 10.6672 11.7952 10.836 11.964C11.0048 12.1327 11.0996 12.3617 11.0996 12.6004C11.0996 12.8391 11.0048 13.068 10.836 13.2368C10.6672 13.4055 10.4383 13.5004 10.1996 13.5004ZM14.9996 19.2004H14.3996V13.2004H15.5996V18.6004C15.5996 18.7595 15.5364 18.9121 15.4239 19.0246C15.3114 19.1371 15.1587 19.2004 14.9996 19.2004ZM14.3996 9.60036V4.80036H14.9996C15.1587 4.80036 15.3114 4.86357 15.4239 4.97609C15.5364 5.08862 15.5996 5.24123 15.5996 5.40036V9.60036H14.3996Z" fill="#808080"/>
                </svg>
                <p>Logout</p>
              </LogoutContainer>
              </span>
          </DrawerConatiner>
        </SwipeableDrawer>
      </TopNavigationContainer>
    }
  </>
  )
}

export default LeftNavigationBar;
